import { buildTranslate } from 'locales'
import { EditorView } from 'prosemirror-view'
import React, { useCallback, useEffect, useRef } from 'react'
import { useLinkBubble, useLinkBubbleActions } from './hooks/useLinkBubble'

const t = buildTranslate('components.rich_text_editor')

type Props = {
  editorId: string
  editorViewRef: React.MutableRefObject<EditorView | null>
  editorContainerRef: React.RefObject<HTMLDivElement>
  mountRef: React.RefObject<HTMLDivElement>
}

const LinkBubble: React.FC<Props> = ({
  editorId,
  editorViewRef,
  editorContainerRef,
  mountRef,
}) => {
  const bubble = useLinkBubble(editorId)
  const { applyLink, removeLink, handleEditorMouseUp } = useLinkBubbleActions(
    editorId,
    editorViewRef,
    editorContainerRef,
    bubble
  )

  const { closeLinkBubble } = bubble
  const handleClose = useCallback(() => {
    closeLinkBubble()
    editorViewRef.current?.focus()
  }, [closeLinkBubble, editorViewRef])

  const bubbleRef = useRef<HTMLDivElement>(null)
  const linkInputRef = useRef<HTMLInputElement>(null)

  // Attach mouseUp listener to editor mount
  useEffect(() => {
    const el = mountRef.current
    if (!el) return
    el.addEventListener('mouseup', handleEditorMouseUp)
    return () => el.removeEventListener('mouseup', handleEditorMouseUp)
  }, [mountRef, handleEditorMouseUp])

  // Auto-focus input in edit mode
  useEffect(() => {
    if (bubble.linkBubble?.mode === 'edit' && linkInputRef.current) {
      linkInputRef.current.focus()
    }
  }, [bubble.linkBubble?.mode])

  // Click-outside to close
  useEffect(() => {
    if (!bubble.linkBubble) return

    const handleClickOutside = (e: MouseEvent) => {
      if (bubbleRef.current && !bubbleRef.current.contains(e.target as Node)) {
        handleClose()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [bubble.linkBubble, handleClose])

  if (!bubble.linkBubble) return null

  const { linkBubble, linkInputValue, isLinkInvalid } = bubble

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      applyLink()
    }
    if (e.key === 'Escape') {
      e.preventDefault()
      e.stopPropagation()
      handleClose()
    }
  }

  return (
    <div
      ref={bubbleRef}
      className="rich-text-editor-link-bubble"
      style={{
        top: linkBubble.top,
        left: Math.max(0, linkBubble.left),
      }}
      data-testid="link-bubble"
    >
      {linkBubble.mode === 'display' ? (
        <>
          <span className="rich-text-editor-link-bubble__url">
            {linkBubble.url}
          </span>
          <button
            type="button"
            className="rich-text-editor-link-bubble__icon-btn"
            onClick={bubble.editLink}
            aria-label={t('edit_link')}
            data-testid="link-edit-btn"
          >
            <i className="fa fa-pencil" />
          </button>
          <button
            type="button"
            className="rich-text-editor-link-bubble__icon-btn"
            onClick={removeLink}
            aria-label={t('remove_link')}
            data-testid="link-delete-btn"
          >
            <i className="fa fa-trash" />
          </button>
        </>
      ) : (
        <>
          <input
            ref={linkInputRef}
            type="url"
            className={`rich-text-editor-link-bubble__input${
              isLinkInvalid ? ' rich-text-editor-link-bubble__input--error' : ''
            }`}
            placeholder={t('url_placeholder')}
            value={linkInputValue}
            onChange={e => bubble.setLinkInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <div className="rich-text-editor-link-bubble__divider" />
          <button
            type="button"
            className="rich-text-editor-link-bubble__action"
            onClick={applyLink}
            disabled={isLinkInvalid}
          >
            {t('link_action')}
          </button>
        </>
      )}
      <div className="rich-text-editor-link-bubble__arrow" />
    </div>
  )
}

export default LinkBubble
